import React, { useEffect, useState } from "react";
import axios from "axios";

const TAG_OPTIONS = [
  { value: "", label: "Aucun" },
  { value: "Lab", label: "Lab" },
  { value: "Live", label: "Live" },
  { value: "Fini", label: "Fini" },
  { value: "Waiting", label: "Waiting" },
  { value: "Démo", label: "Démo" },
  { value: "Autres", label: "Autres" },
];

const HotelGeneralSettings = ({ masterId, initialTag, initialCity, token, API_URL, onUpdate }) => {
  const [hotelTag, setHotelTag] = useState(initialTag || "");
  const [initialHotelTag, setInitialHotelTag] = useState(initialTag || "");
  const [hotelCity, setHotelCity] = useState(initialCity || "");
  const [initialHotelCity, setInitialHotelCity] = useState(initialCity || "");
  const [loading, setLoading] = useState(false);
  const [savingTag, setSavingTag] = useState(false);
  const [savingCity, setSavingCity] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!masterId || !token) return;

    let cancelled = false;
    const fetchProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await axios.get(`${API_URL}/hotels/${masterId}/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && data?.success === "OK" && data.result) {
          const tag = data.result.tag || "";
          const city = data.result.city || "";
          setHotelTag(tag);
          setInitialHotelTag(tag);
          setHotelCity(city);
          setInitialHotelCity(city);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || "Impossible de charger le tag de l'hôtel");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProfile();
    return () => {
      cancelled = true;
    };
  }, [masterId, token, API_URL]);

  const saveHotelTag = async () => {
    if (!masterId || !token || hotelTag === initialHotelTag) return;

    setSavingTag(true);
    setError(null);
    setSuccess(null);
    try {
      await axios.patch(
        `${API_URL}/hotels/${masterId}/tag`,
        { tag: hotelTag || null },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setInitialHotelTag(hotelTag);
      setSuccess("Tag enregistré");
      setTimeout(() => setSuccess(null), 2500);
      onUpdate?.();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Impossible d'enregistrer le tag");
    } finally {
      setSavingTag(false);
    }
  };

  const saveHotelCity = async () => {
    const trimmedCity = hotelCity.trim();
    if (!masterId || !token || trimmedCity === initialHotelCity) return;
    if (!trimmedCity) {
      setError("La ville de l'hôtel est requise");
      return;
    }

    setSavingCity(true);
    setError(null);
    setSuccess(null);
    try {
      await axios.patch(
        `${API_URL}/hotels/${masterId}/profile`,
        { city: trimmedCity },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setHotelCity(trimmedCity);
      setInitialHotelCity(trimmedCity);
      setSuccess("Ville enregistrée");
      setTimeout(() => setSuccess(null), 2500);
      onUpdate?.();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Impossible d'enregistrer la ville");
    } finally {
      setSavingCity(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Réglages de l'hôtel</h3>
          <p className="mt-1 text-sm text-gray-500">Paramètres généraux liés à l'établissement.</p>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label htmlFor="general-hotel-tag" className="block text-sm font-medium text-gray-700 mb-1">
              Tag de l'hôtel
            </label>
            <div className="flex flex-wrap items-center gap-3">
              <select
                id="general-hotel-tag"
                value={hotelTag}
                onChange={(e) => setHotelTag(e.target.value)}
                disabled={loading || savingTag}
                className="min-w-[220px] px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-60"
              >
                {TAG_OPTIONS.map((option) => (
                  <option key={option.value || "empty"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={saveHotelTag}
                disabled={loading || savingTag || hotelTag === initialHotelTag}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {savingTag ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="general-hotel-city" className="block text-sm font-medium text-gray-700 mb-1">
              Ville
            </label>
            <div className="flex flex-wrap items-center gap-3">
              <input
                id="general-hotel-city"
                type="text"
                value={hotelCity}
                onChange={(e) => setHotelCity(e.target.value)}
                disabled={loading || savingCity}
                className="min-w-[220px] px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-60"
                placeholder="Ex: Paris"
              />

              <button
                type="button"
                onClick={saveHotelCity}
                disabled={loading || savingCity || !hotelCity.trim() || hotelCity.trim() === initialHotelCity}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {savingCity ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>

            {loading && <p className="mt-2 text-sm text-gray-500">Chargement...</p>}
            {success && <p className="mt-2 text-sm text-green-700">{success}</p>}
            {error && <p className="mt-2 text-sm text-red-700">{error}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotelGeneralSettings;
